import {
  ChangeDetectionStrategy,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DATE_FORMATS,
  MAT_NATIVE_DATE_FORMATS,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';

import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ApiKey } from '@rhapsody/ngx-api-key-client';
import { NgxMatTimepickerModule } from 'ngx-mat-timepicker';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'rh-change-expiration-dialog',
    imports: [
        MatButtonModule,
        MatDatepickerModule,
        MatDialogActions,
        MatDialogClose,
        MatDialogContent,
        MatDialogTitle,
        MatFormFieldModule,
        MatInputModule,
        NgxMatTimepickerModule,
        ReactiveFormsModule,
    ],
    providers: [
        provideNativeDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MAT_NATIVE_DATE_FORMATS },
    ],
    templateUrl: './change-expiration-dialog.component.html',
    styleUrl: './change-expiration-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChangeExpirationDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<ChangeExpirationDialogComponent>);
  readonly data = inject<ApiKey>(MAT_DIALOG_DATA);

  readonly date = new FormControl<string | null>(null);
  readonly dateTime = new FormControl<string>('');
  readonly time = new FormControl<string | null>('12:59 PM');
  private readonly onDestroy = new Subject();

  ngOnInit(): void {
    if (this.data?.expires_at) {
      const expiresAt = new Date(this.data.expires_at);
      this.date.patchValue(expiresAt.toISOString());
      this.extractTime(expiresAt);
    }

    this.time.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.combineDateTime());
    this.date.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(() => this.combineDateTime());
  }

  ngOnDestroy(): void {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }

  public onNoClick(): void {
    this.dialogRef.close();
  }

  public extractTime(expiresAt: Date): string {
    // Extract and format the time
    let hours = expiresAt.getHours();
    const minutes = expiresAt.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    const formattedTime = hours + ':' + minutesStr + ' ' + ampm;

    // Patch the formatted time to the time control
    this.time.patchValue(formattedTime);
    return formattedTime;
  }

  private combineDateTime(): void {
    const dateValue = this.date.value;
    const timeValue = this.time.value;

    if (dateValue && timeValue) {
      const date = new Date(dateValue);
      const [time, modifier] = timeValue.split(' ');
      let [hours, minutes] = time.split(':').map(Number);

      if (modifier === 'PM' && hours < 12) {
        hours += 12;
      } else if (modifier === 'AM' && hours === 12) {
        hours = 0;
      }

      date.setHours(hours, minutes, 0, 0);
      const combinedDateTimeISO = date.toISOString();
      this.dateTime.patchValue(combinedDateTimeISO);

      const combinedDateTimeFormatted = date.toLocaleString();
      console.info('Combined DateTime (ISO):', combinedDateTimeISO);
      console.info('Combined DateTime (Formatted):', combinedDateTimeFormatted);
    }
  }
}
